import { InputProps } from 'antd';
import { FC, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as S from './style';
import { alertRequest } from '~/store/modules/alert/actions';

interface Props extends InputProps {
  onChange: (value: any) => void;
  consumed?: number;
  max?: number;
  maxErrorMessage?: string;
  min?: number;
  minErrorMessage?: string;
  timeout?: number;
  value: string | number | undefined;
}

export const InputTable: FC<Props> = ({
  onChange,
  consumed,
  max,
  maxErrorMessage,
  min,
  minErrorMessage,
  timeout = 500,
  value,
  ...props
}) => {
  const dispatch = useDispatch();
  const [displayValue, setDisplayValue] = useState<
    string | number | undefined
  >();

  const changeValue = useCallback(
    (newValue: number) => {
      if (max !== undefined) {
        if (Number.isNaN(max)) {
          throw new Error(`A propriedade max(${max}) deve ser do tipo número!`);
        }
        if (!Number.isNaN(max) && newValue > max) {
          dispatch(
            alertRequest(
              maxErrorMessage ?? `A quantidade máxima é ${max}`,
              'error'
            )
          );
          onChange(max);
          setDisplayValue(max);
          return;
        }
        onChange(newValue);
        setDisplayValue(newValue);
      }
      if (min !== undefined) {
        if (Number.isNaN(min)) {
          throw new Error(`A propriedade min(${min}) deve ser do tipo número!`);
        }
        if (!Number.isNaN(min) && newValue < min) {
          dispatch(
            alertRequest(
              minErrorMessage ?? `A quantidade mínima é ${min}`,
              'error'
            )
          );
          onChange(min);
          setDisplayValue(min);
          return;
        }
        onChange(newValue);
        setDisplayValue(newValue);
        return;
      }
      onChange(newValue);
      setDisplayValue(newValue);
    },
    [dispatch, max, maxErrorMessage, min, minErrorMessage, onChange]
  );

  useEffect(() => {
    if (value !== displayValue) {
      setDisplayValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (displayValue !== value) {
        if (displayValue && !Number.isNaN(displayValue)) {
          changeValue(Number(displayValue));
          return;
        }
        if (consumed) {
          onChange(consumed);
          setDisplayValue(consumed);
          dispatch(
            alertRequest(
              `A oferta tem ${consumed} unidades consumidas`,
              'error'
            )
          );
          return;
        }
        onChange(0);
        setDisplayValue(0);
      }
    }, timeout);
    return () => clearTimeout(handler);
  }, [displayValue]);

  return (
    <S.InputTable
      placeholder="-"
      {...props}
      onChange={e => {
        setDisplayValue(Number(e.target.value));
      }}
      min="0"
      style={{ width: '100%', minWidth: 65, ...props.style }}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={!!displayValue && !Number.isNaN(displayValue) ? displayValue : ''}
      autoComplete="off"
    />
  );
};
